import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { 
  ChevronRight, 
  Search, 
  Building2, 
  Mail, 
  BarChart3, 
  Users, 
  FileCheck, 
  Bell, 
  Link2, 
  Shield, 
  Eye, 
  Clock,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Database,
  Lock
} from 'lucide-react';
import SEO, { getServiceSchema } from '../components/SEO';
import { trackAIServicePageViewed, trackKasPawsCaseStudyOpened, trackAIProjectContactClicked } from '../lib/posthog';

const AISystemsAutomation = () => {
  const navigate = useNavigate();

  // Track page view on mount
  useEffect(() => {
    trackAIServicePageViewed('hero');
  }, []);

  // Handle CTA clicks with tracking
  const handleContactClick = (source) => {
    trackAIProjectContactClicked(source);
    navigate('/contact?service=ai-automation');
  };

  const handleCaseStudyClick = () => {
    trackKasPawsCaseStudyOpened();
    document.getElementById('kaspaws-case-study')?.scrollIntoView({ behavior: 'smooth' });
  };

  // SEO structured data
  const serviceSchema = getServiceSchema({
    name: 'AI Systems & Business Automation',
    description: 'Custom AI-assisted operational systems, workflow automation, CRM integrations, and business dashboards. RC Studios builds systems that help businesses research opportunities, organize work, and follow through.',
    url: 'https://rcstudios.net/services/ai-systems-automation',
    image: 'https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/lbi5i5zq_image.png',
    areaServed: 'United States',
    priceRange: 'Contact for quote'
  });

  // Business problems we solve
  const businessProblems = [
    { icon: Search, title: 'Lead & Opportunity Research', desc: 'Find and organize potential customers using Google Places and authorized public sources.' },
    { icon: Building2, title: 'Public Business Information', desc: 'Research and verify business details, contacts, and website information.' },
    { icon: Database, title: 'Custom Operations Dashboards', desc: 'One secure workspace to view, filter, and act on your business data.' },
    { icon: Users, title: 'CRM Organization & Integrations', desc: 'Connect your existing tools and keep customer data organized automatically.' },
    { icon: FileCheck, title: 'Personalized Draft Preparation', desc: 'AI-assisted outreach drafts based on real prospect information.' },
    { icon: Eye, title: 'Human Review & Approval', desc: 'Every consequential action requires your explicit approval before execution.' },
    { icon: Mail, title: 'Email & Reply Tracking', desc: 'Monitor delivery, opens, and responses from one place.' },
    { icon: BarChart3, title: 'Conversion & Stage Reporting', desc: 'Understand which outreach works and where prospects are in your pipeline.' },
    { icon: Bell, title: 'Internal Notifications', desc: 'Get alerts when important events happen — replies, conversions, or issues.' },
    { icon: Link2, title: 'Custom Tool Integrations', desc: 'Connect the software you already use into a unified workflow.' },
  ];

  // Process steps
  const processSteps = [
    { step: '01', title: 'Discover', desc: 'Identify the business problem and repetitive work slowing you down.' },
    { step: '02', title: 'Design', desc: 'Map the workflow, data flow, approval points, and user experience.' },
    { step: '03', title: 'Build', desc: 'Create the platform, dashboard, automation rules, and integrations.' },
    { step: '04', title: 'Test', desc: 'Verify real scenarios, permissions, mobile behavior, and failure handling.' },
    { step: '05', title: 'Launch & Improve', desc: 'Introduce the system carefully, measure results, and expand as you grow.' },
  ];

  // Trust & control features
  const trustFeatures = [
    { icon: CheckCircle2, text: 'Human approval before consequential actions' },
    { icon: Lock, text: 'Private administrative access' },
    { icon: Eye, text: 'Clear audit and activity history' },
    { icon: Clock, text: 'Controlled sending and rate limits' },
    { icon: Shield, text: 'No fabricated contact information' },
    { icon: FileCheck, text: 'No unsupported marketing claims' },
    { icon: Database, text: 'Respect for website terms and data access' },
    { icon: Lock, text: 'Secure integrations with minimum permissions' },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* SEO Meta Tags - Uses dynamic OG image generator */}
      <SEO
        title="AI Systems & Business Automation"
        description="RC Studios builds custom AI-assisted operational systems, workflow automation, CRM integrations, and business dashboards. Reduce repetitive work while keeping important decisions under your control."
        path="/services/ai-systems-automation"
        pageType="Service"
        jsonLd={serviceSchema}
      />

      {/* ════════════════════════════════════════════════════════════════════
          HERO SECTION
          ════════════════════════════════════════════════════════════════════ */}
      <section className="relative bg-[#050816] text-white pt-20 pb-16 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#050816] to-[#050816]" />
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-[#C6A14A]/5 rounded-full blur-[120px] -translate-y-1/2" />
        
        <div className="container mx-auto px-6 max-w-[1200px] relative z-10">
          <div className="max-w-3xl">
            <p className="text-[#C6A14A] text-xs font-bold uppercase tracking-[0.3em] mb-4" data-testid="ai-systems-eyebrow">
              AI Systems & Automation
            </p>
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-6 leading-tight" data-testid="ai-systems-heading">
              Custom systems that help your business find opportunities, organize work, and follow through.
            </h1>
            <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
              RC Studios combines custom platform development, thoughtful automation, integrations, and AI-assisted workflows to reduce repetitive work while keeping important decisions under your control.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => handleContactClick('hero_primary_cta')}
                className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-semibold transition-all duration-200"
                data-testid="ai-systems-primary-cta"
              >
                Discuss an AI Automation Project
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={handleCaseStudyClick}
                className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all duration-200"
                data-testid="ai-systems-secondary-cta"
              >
                See the KasPaws Case Study
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          BUSINESS PROBLEMS SECTION
          ════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-[#FAF9F6]">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="text-center mb-12">
            <p className="text-[#C6A14A] text-xs font-bold uppercase tracking-[0.3em] mb-3">
              What We Build
            </p>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#050816] mb-4">
              Business problems we can help solve
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              From research and organization to communication and reporting — we build systems that handle the repetitive work so you can focus on decisions that matter.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {businessProblems.map((problem, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#C6A14A]/30 hover:shadow-lg transition-all duration-300 group"
                data-testid={`business-problem-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-[#C6A14A]/10 flex items-center justify-center mb-4 group-hover:bg-[#C6A14A]/20 transition-colors">
                  <problem.icon className="w-6 h-6 text-[#C6A14A]" />
                </div>
                <h3 className="font-semibold text-[#050816] mb-2">{problem.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{problem.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          KASPAWS OPS CASE STUDY
          ════════════════════════════════════════════════════════════════════ */}
      <section id="kaspaws-case-study" className="py-16 md:py-20 bg-[#050816]">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Screenshot */}
            <div className="order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/lbi5i5zq_image.png"
                  alt="KP OPS - Secure Operations Center for KasPaws"
                  className="w-full h-auto"
                  data-testid="kpops-screenshot"
                />
              </div>
            </div>

            {/* Right: Content */}
            <div className="order-1 lg:order-2">
              <p className="text-[#C6A14A] text-xs font-bold uppercase tracking-[0.3em] mb-3">
                Case Study
              </p>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-white mb-6">
                KasPaws & KP OPS
              </h2>
              <p className="text-white/70 text-base leading-relaxed mb-6">
                KasPaws is proof that RC Studios can build both a customer-facing platform and a private operational system. The public marketplace connects pet parents with trusted sitters. Behind it, KP OPS powers the business operations.
              </p>

              {/* Two columns: Customer-facing vs Internal */}
              <div className="grid sm:grid-cols-2 gap-6 mb-8">
                {/* Customer-facing */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#C6A14A]" />
                    Customer Platform
                  </h3>
                  <ul className="space-y-2 text-white/60 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      Business profiles & discovery
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      Booking & request workflows
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      Payments & subscriptions
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      Customer & provider dashboards
                    </li>
                  </ul>
                </div>

                {/* Internal KP OPS */}
                <div className="bg-[#C6A14A]/10 rounded-xl p-5 border border-[#C6A14A]/20">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-[#C6A14A]" />
                    KP OPS (Internal)
                  </h3>
                  <ul className="space-y-2 text-white/70 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      Google Places & public-source research
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      Website & contact verification
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      Evidence-backed opportunity ID
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      Personalized outreach drafting
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      Exact-content approval before send
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      Reply detection & stage tracking
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#C6A14A] mt-2 flex-shrink-0" />
                      City/state outreach reporting
                    </li>
                  </ul>
                </div>
              </div>

              <p className="text-white/50 text-sm italic mb-6">
                KP OPS is AI-assisted, rule-based where appropriate, supervised, evidence-backed, and approval-controlled — not fully autonomous.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/services/website-design"
                  className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#D4AF5A] text-sm font-medium transition-colors"
                  data-testid="kaspaws-platform-link"
                >
                  View KasPaws Platform
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          HOW WE WORK SECTION
          ════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="text-center mb-12">
            <p className="text-[#C6A14A] text-xs font-bold uppercase tracking-[0.3em] mb-3">
              Our Process
            </p>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#050816] mb-4">
              How RC Studios works
            </h2>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                className="text-center group"
                data-testid={`process-step-${index}`}
              >
                <div className="relative mb-4">
                  <span className="text-5xl font-serif text-[#C6A14A]/20 group-hover:text-[#C6A14A]/40 transition-colors">
                    {step.step}
                  </span>
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 right-0 w-full h-px bg-gradient-to-r from-[#C6A14A]/20 to-transparent translate-x-1/2" />
                  )}
                </div>
                <h3 className="font-semibold text-[#050816] mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          TRUST & CONTROL SECTION
          ════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-[#FAF9F6]">
        <div className="container mx-auto px-6 max-w-[1000px]">
          <div className="text-center mb-12">
            <p className="text-[#C6A14A] text-xs font-bold uppercase tracking-[0.3em] mb-3">
              Built-in Safeguards
            </p>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#050816] mb-4">
              Trust and control by design
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              RC Studios designs systems with safeguards that keep you in control of important decisions.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {trustFeatures.map((feature, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100"
                data-testid={`trust-feature-${index}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#C6A14A]/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-[#C6A14A]" />
                </div>
                <p className="text-[#050816] text-sm font-medium pt-2">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          CONTACT CTA SECTION
          ════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-20 bg-[#050816]">
        <div className="container mx-auto px-6 max-w-[800px] text-center">
          <Sparkles className="w-10 h-10 text-[#C6A14A] mx-auto mb-6" />
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-white mb-4">
            What takes too much time in your business?
          </h2>
          <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
            Tell Gregory about the research, follow-up, reporting, or administrative work slowing your company down. RC Studios can help determine whether a custom website, connected workflow, or AI-assisted operations system would solve it.
          </p>
          <Button 
            onClick={() => handleContactClick('bottom_cta')}
            className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] rounded-lg h-12 px-10 text-sm uppercase tracking-widest font-semibold transition-all duration-200"
            data-testid="ai-systems-contact-cta"
          >
            Discuss an AI Automation Project
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-12 bg-[#FAF9F6] border-t border-gray-100">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <Link 
              to="/services/website-design" 
              className="text-gray-600 hover:text-[#C6A14A] transition-colors"
            >
              Website & Platform Development →
            </Link>
            <Link 
              to="/services/digital-persona-development" 
              className="text-gray-600 hover:text-[#C6A14A] transition-colors"
            >
              AI Content & Digital Personas →
            </Link>
            <Link 
              to="/pricing/creative-digital" 
              className="text-gray-600 hover:text-[#C6A14A] transition-colors"
            >
              View Creative & Digital Pricing →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AISystemsAutomation;
